import { useMemo } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import s from './Pagination.module.css';

type PageEntry = number | '…';

type PaginationProps = {
  page: number;
  setPage: (p: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (n: number) => void;
  total: number;
  rowsPerPageOptions?: number[];
};

function buildPageButtons(page: number, lastPage: number): PageEntry[] {
  const total = lastPage + 1;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);

  const out = new Set<number>([0, lastPage, page, page - 1, page + 1]);
  if (page <= 2) [1, 2, 3].forEach((p) => out.add(p));
  if (page >= lastPage - 2) [lastPage - 1, lastPage - 2, lastPage - 3].forEach((p) => out.add(p));

  const list = [...out].filter((p) => p >= 0 && p <= lastPage).sort((a, b) => a - b);
  const result: PageEntry[] = [];
  for (let i = 0; i < list.length; i++) {
    if (i > 0 && list[i] - list[i - 1] > 1) result.push('…');
    result.push(list[i]);
  }
  return result;
}

function Pagination({
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  total,
  rowsPerPageOptions = [10, 25, 50],
}: PaginationProps) {
  const start = total === 0 ? 0 : page * rowsPerPage + 1;
  const end = Math.min(total, (page + 1) * rowsPerPage);
  const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  const buttons = useMemo(() => buildPageButtons(page, lastPage), [page, lastPage]);

  return (
    <div className={s.pagination}>
      <div className={s.rowsPerPage}>
        <span>Rows</span>
        <div className={s.selectWrap}>
          <select
            className={s.select}
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}>
            {rowsPerPageOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <KeyboardArrowDownIcon className={s.selectChevron} sx={{ fontSize: 16 }} />
        </div>
      </div>

      <div className={s.range}>
        <span className={s.rangeStrong}>
          {start}–{end}
        </span>
        <span> of {total.toLocaleString()}</span>
      </div>

      <div className={s.pages}>
        <button
          type="button"
          className={s.pageBtn}
          onClick={() => setPage(page - 1)}
          disabled={page === 0}
          aria-label="Previous page">
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </button>

        {buttons.map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} className={s.ellipsis}>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`${s.pageBtn} ${p === page ? s.pageBtnActive : ''}`}
              onClick={() => setPage(p)}
              aria-current={p === page ? 'page' : undefined}>
              {p + 1}
            </button>
          ),
        )}

        <button
          type="button"
          className={s.pageBtn}
          onClick={() => setPage(page + 1)}
          disabled={page >= lastPage}
          aria-label="Next page">
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </button>
      </div>
    </div>
  );
}

export default Pagination;
